import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Sparkles, Crown, Loader2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { slugForCategory } from "@/lib/categoryHeroes";

function timeUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return null;
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  if (days > 0) return `${days}d ${hours}h`;
  return `${hours}h`;
}

export default function FeatureSlotCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/featured/me/status`, { withCredentials: true });
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const claim = async () => {
    if (!status?.category) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/featured/${encodeURIComponent(status.category)}/claim`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data?.message || "Featured!");
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim slot");
    } finally {
      setBusy(false);
    }
  };

  const release = async () => {
    if (!status?.category) return;
    if (!window.confirm("Release your featured slot now? Other businesses will be able to claim it.")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/featured/${encodeURIComponent(status.category)}`, { withCredentials: true });
      toast.success("Slot released");
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not release slot");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-6 flex items-center justify-center text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading featured slot…
      </div>
    );
  }

  if (!status) return null;

  const slug = status.category ? slugForCategory(status.category) : null;
  const claimed = !!status.featured;
  const remaining = claimed ? timeUntil(status.featured.expires_at) : null;

  return (
    <div
      className="rounded-2xl border border-tbc-gold/30 bg-gradient-to-br from-tbc-navy to-[#0E2541] text-white p-6 shadow-md"
      data-testid="feature-slot-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-tbc-gold/15 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-tbc-gold" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-tbc-gold">Premium perk</p>
            <h3 className="font-heading text-lg">Featured Business Slot</h3>
          </div>
        </div>
        {claimed && (
          <span
            className="inline-flex items-center gap-1 bg-tbc-gold text-tbc-navy text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
            data-testid="feature-slot-live-pill"
          >
            <Sparkles className="w-3 h-3" /> Live
          </span>
        )}
      </div>

      <p className="text-sm text-gray-300 mt-3">
        Take over the spotlight slot on the{" "}
        <span className="text-white font-medium">{status.category || "your"}</span> category page for a full week.
      </p>

      {!status.eligible ? (
        <div className="mt-4 bg-black/30 rounded-xl p-4">
          <p className="text-sm text-gray-300 flex items-center gap-2">
            <Crown className="w-4 h-4 text-tbc-gold" />
            Upgrade to Premium or join the Founding Member tier to unlock this perk.
          </p>
          <Link to="/pricing">
            <Button
              className="mt-3 bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90"
              data-testid="feature-slot-upgrade-cta"
            >
              See Premium plans
            </Button>
          </Link>
        </div>
      ) : claimed ? (
        <div className="mt-4">
          <div className="flex items-center gap-2 text-sm text-gray-300">
            <Clock className="w-4 h-4 text-tbc-gold" />
            <span data-testid="feature-slot-remaining">{remaining || "expiring soon"} remaining</span>
          </div>
          <div className="flex gap-2 mt-4">
            {slug && (
              <Link to={`/category/${slug}`} className="flex-1">
                <Button
                  variant="outline"
                  className="w-full border-tbc-gold/40 text-white hover:bg-white/10 bg-transparent"
                  data-testid="feature-slot-view-cta"
                >
                  View placement
                </Button>
              </Link>
            )}
            <Button
              onClick={release}
              disabled={busy}
              variant="outline"
              className="border-red-300/40 text-red-200 hover:bg-red-500/10 bg-transparent"
              data-testid="feature-slot-release"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Release"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <Button
            onClick={claim}
            disabled={busy}
            className="w-full bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90 font-semibold"
            data-testid="feature-slot-claim"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Claim featured slot (7 days)
          </Button>
          <p className="text-[11px] text-gray-400 mt-2 text-center">
            Auto-expires after 7 days so the slot stays rotating and valuable.
          </p>
        </div>
      )}
    </div>
  );
}
